import React, { useMemo } from "react";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { actions } from "store/actions";
import Table from "components/Table";
import { ReactComponent as AddBox } from "images/profile/box-add.svg";
import { ReactComponent as DangerIcon } from "images/profile/danger.svg";
import { ReactComponent as DownloadDocument } from "images/profile/document-download.svg";
import { ReactComponent as CloseSquare } from "images/profile/close-square.svg";
import { ReactComponent as TickSquare } from "images/profile/tick-square.svg";
import ActionTooltip from "components/ActionTooltip";
import EmptyState from "pages/projects/common/EmptyState";
import { poStatusesOptions, PO_SENT_STATUS } from "utils/constantValues";
import Badges from "components/badege";
import PoRespondModal from "pages/projects/common/PoRespondModal";
import useNotify from "hooks/useNotify";

const TasksTable = ({
  tasks,
  isLoading,
  viewReferences,
  uploadDeliverables,
  fetchTasks = () => {},
}) => {
  const [t] = useTranslation();
  const dispatch = useDispatch();

  // Modal state
  const [openRespond, setOpenRespond] = React.useState(false);
  const [respondAcceptance, setRespondAcceptance] = React.useState(false);
  const [selectedAssigneeId, setSelectedAssigneeId] = React.useState("");
  const [respondLoading, setRespondLoading] = React.useState(false);
  const { notifyError, notifySuccess } = useNotify();

  const columns = useMemo(
    () => [
      {
        Header: t("taskNumberHeader"),
        accessor: "orderNumber",
        Cell: ({ value }) => (
          <div className="font-semibold text-[#1D1B22] text-[16px]">
            {value}
          </div>
        ),
      },
      {
        Header: t("poHeader"),
        accessor: "poNumber",
        Cell: ({ value, row }) => {
          const softDeleted = row.original.isSoftDeleted === true;
          return softDeleted ? (
            <div className="inline-flex items-center gap-1.5 !text-red-500">
              <ActionTooltip label={t("poSoftDeletedTooltip")}>
                <DangerIcon
                  className="h-[18px] w-[18px] shrink-0 text-red-500"
                  aria-hidden
                />
              </ActionTooltip>
              <ActionTooltip label={t("downloadPo")}>
                <button
                  type="button"
                  className="whitespace-nowrap font-medium text-[16px] text-red-500 underline cursor-pointer"
                  onClick={() => dispatch(actions.downloadPo(row.original.id))}
                >
                  {value}
                </button>
              </ActionTooltip>
            </div>
          ) : (
            <ActionTooltip label={t("downloadPo")}>
              <button
                type="button"
                className="whitespace-nowrap font-medium text-[#1D1B22] text-[16px] underline cursor-pointer"
                onClick={() => dispatch(actions.downloadPo(row.original.id))}
              >
                {value}
              </button>
            </ActionTooltip>
          );
        },
      },
      {
        Header: t("fileNameHeader"),
        accessor: "parentFile.name",
        Cell: ({ row }) => {
          const parentFile = row?.original?.parentFile;
          if (!parentFile?.name) return <div className="text-[#666666]">-</div>;
          return (
            <ActionTooltip label={parentFile?.name ?? ""}>
              <button
                className={`font-medium text-[#1D1B22] text-[16px] ${
                  parentFile.id ? "underline cursor-pointer" : "cursor-default"
                } max-w-[220px] truncate text-left`}
                title={parentFile.name}
                onClick={() => {
                  if (!parentFile?.id) return;
                  dispatch(actions.downloadParentFile(parentFile.id));
                }}
              >
                {parentFile.name}
              </button>
            </ActionTooltip>
          );
        },
      },
      {
        Header: t("taskStatusHeader"),
        accessor: "assigneeStatus",
        Cell: ({ value }) => {
          return (
            <Badges
              label={poStatusesOptions[value]?.label ?? value}
              variant={poStatusesOptions[value]?.variant ?? "primary"}
            />
          );
        },
      },
      {
        Header: t("skill"),
        accessor: "role",
        Cell: ({ value }) => <div className="text-[#1D1B22]">{value}</div>,
      },
      {
        Header: t("totalVolumeHeader"),
        accessor: "unitCount",
        Cell: (props) => {
          const unitCount = props.data[props.row.id]?.unitCount;
          const uom = props.data[props.row.id]?.uom;
          return <div className="text-[#1D1B22]">{`${unitCount} ${uom}`}</div>;
        },
      },
      {
        Header: t("deadlineHeader"),
        accessor: "deadline",
        Cell: ({ value }) => {
          if (!value) return <div className="text-[#666666]">-</div>;
          const date = new Date(value);
          const formattedDate = date.toLocaleDateString("en-GB", {
            day: "2-digit",
            month: "2-digit",
            year: "numeric",
          });
          const formattedTime = date.toLocaleTimeString("en-US", {
            hour: "2-digit",
            minute: "2-digit",
            hour12: true,
          });
          return (
            <div className="text-[#1D1B22]">
              <div className="font-medium">{formattedDate}</div>
              <div className="text-[12px] text-[#666666]">{formattedTime}</div>
            </div>
          );
        },
      },
      {
        id: "action",
        Header: "",
        Cell: ({ row }) => (
          <div className="flex items-center gap-[15px]">
            {row.original.workSpaceUrl && (
              <ActionTooltip label={t("workSpace")}>
                <div
                  className="flex py-[2px] px-[6px] items-center gap-[6px] border rounded-[6px] border-[#1E4A6B] bg-[#fff] cursor-pointer"
                  onClick={() =>
                    window.open(row.original.workSpaceUrl, "_blank")
                  }
                >
                  <div className="text-[#1E4A6B] text-[14px] font-semibold mt-[2px]">
                    {t("workSpace")}
                  </div>
                </div>
              </ActionTooltip>
            )}
            {PO_SENT_STATUS === row.original.assigneeStatus && (
              <>
                <ActionTooltip label={t("acceptTask")}>
                  <button
                    onClick={() => {
                      const assigneeId = row.original.assigneeId;
                      if (assigneeId) {
                        setSelectedAssigneeId(assigneeId);
                        setRespondAcceptance(true);
                        setOpenRespond(true);
                      }
                    }}
                  >
                    <TickSquare />
                  </button>
                </ActionTooltip>

                <ActionTooltip label={t("rejectTaskTitle")}>
                  <button
                    onClick={() => {
                      const assigneeId = row.original.assigneeId;
                      if (assigneeId) {
                        setSelectedAssigneeId(assigneeId);
                        setRespondAcceptance(false);
                        setOpenRespond(true);
                      }
                    }}
                  >
                    <CloseSquare />
                  </button>
                </ActionTooltip>
              </>
            )}
            <ActionTooltip label={t("uploadDeliverables")}>
              <button
                onClick={() => uploadDeliverables(row.original)}
                className="p-1 hover:bg-gray-100 rounded"
              >
                <AddBox />
              </button>
            </ActionTooltip>
            <ActionTooltip label={t("viewReferences")}>
              <button
                onClick={() => viewReferences(row.original)}
                className="p-1 hover:bg-gray-100 rounded"
              >
                <DownloadDocument />
              </button>
            </ActionTooltip>
          </div>
        ),
      },
    ],
    [uploadDeliverables, viewReferences, dispatch, t]
  );

  if (isLoading) {
    return (
      <div className="flex justify-center items-center py-8">
        <div className="text-gray-500">{t("loadingTasks")}</div>
      </div>
    );
  }

  if (!tasks || tasks.length === 0) {
    return <EmptyState label={t("emptyTasks")} />;
  }

  return (
    <div className="w-full">
      <Table columns={columns} data={tasks} />

      {/* Confirmation Modal */}
      <PoRespondModal
        title={t("poRespondTitle")}
        isOpen={openRespond}
        onClose={() => setOpenRespond(false)}
        onSubmit={(assigneeId, acceptance) => {
          setRespondLoading(true);
          dispatch(
            actions.respondToPo(assigneeId, acceptance, (success, err) => {
              setRespondLoading(false);
              if (success) {
                fetchTasks();
                setOpenRespond(false);
                notifySuccess(acceptance ? t("poAcceptMsg") : t("rejectTask"));
              } else {
                notifyError(
                  err?.response?.data?.message ??
                    err?.data?.error ??
                    err?.message ??
                    err?.error ??
                    "Something went wrong"
                );
              }
            })
          );
        }}
        isLoading={respondLoading}
        acceptance={respondAcceptance}
        assigneeId={selectedAssigneeId}
      />
    </div>
  );
};

TasksTable.propTypes = {
  tasks: PropTypes.array.isRequired,
  isLoading: PropTypes.bool,
  viewReferences: PropTypes.func,
  uploadDeliverables: PropTypes.func,
  fetchTasks: PropTypes.func,
};

TasksTable.defaultProps = {
  isLoading: false,
  viewReferences: () => {},
  uploadDeliverables: () => {},
};

export default TasksTable;
